import { Alert, Stack } from "@mui/material";
import { useMemo, useState } from "react";

import NoDataAlert from "@/common/components/AppAlerts/NoDataAlert";
import { VehicleVisualModelHelper } from "@/common/helpers/entity/vehicleVisualModel";
import { Mapper } from "@/common/mapping/mapper";
import { GenericDamage } from "@/common/ts/visualModel";
import { VehicleArea, VehicleProjection } from "@/core/api/generated/v1";

import { VisualModelSvgViewer, VisualModelSvgViewerProps } from "../VisualModelSvgViewer";
import { GenericDamageViewModal } from "./GenericDamageViewModal";

interface Props<TDamage extends GenericDamage = GenericDamage> {
  damage: TDamage;
  isViewEnabled?: boolean;
  visualModelSvgViewerProps?: Partial<VisualModelSvgViewerProps>;
}

/** Displays the damage on single visual model. */
export function VisualModelViewerOfGenericDamage<TDamage extends GenericDamage = GenericDamage>({
  damage,
  isViewEnabled = false,
  visualModelSvgViewerProps,
}: Props<TDamage>) {
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);

  const point = useMemo(
    () => Mapper.visualModel.mapGenericDamageToVisualModelPoint(damage),
    [damage],
  );

  if (!damage.visualModelId) {
    return (
      <NoDataAlert
        title='This damage has no visual modal.'
        description="It's likely it was added without placing a point."
      />
    );
  }

  return (
    <Stack gap={1}>
      {(!point || !damage.point || !damage.area || !damage.projection) && (
        <Alert severity='warning'>{`This damage wasn't marked on the visual modal.`}</Alert>
      )}

      <VisualModelSvgViewer
        {...visualModelSvgViewerProps}
        visualModelId={damage.visualModelId}
        visualModel={undefined}
        imageSelector={{
          area: damage.point ? damage.area || VehicleArea.Exterior : VehicleArea.Exterior,
          projection: damage.point
            ? damage.projection || VehicleProjection.Front
            : VehicleProjection.Front,
        }}
        points={point ? [point] : []}
        svgViewerProps={{
          ...visualModelSvgViewerProps?.svgViewerProps,
          svgProps: {
            ...visualModelSvgViewerProps?.svgViewerProps?.svgProps,
            cursor: "default",
            point: {
              cursor: isViewEnabled ? "pointer" : "default",
            },
          },
          defaultPointStyle: VehicleVisualModelHelper.defaultSvgPointStyleForDamage,
          isZoomEnabled: true,
          isResizeEnabled: true,
          isPointDragEnabled: false,
        }}
        withVisualModelInfo
        onPointClicked={() => {
          setIsViewModalOpen(true);
        }}
      />

      {/* Damage view modal */}
      {isViewEnabled && (
        <GenericDamageViewModal
          open={isViewModalOpen}
          onClose={() => {
            setIsViewModalOpen(false);
          }}
          viewProps={{
            damage: damage,
          }}
        />
      )}
    </Stack>
  );
}
